import { AxiosInstance } from 'axios'
import { createOptimizedAPI } from '../utils'
import {
  IConceptSearchResultItem,
  IStandardizedConceptAllLanguages,
  IGetConceptsResponse,
  ConceptRelations,
  IConceptLabel,
  IConceptDescription,
  IConceptImport
} from '../../../types/concept.types'

interface IUserContributions {
  normalized_contributions: {
    comment: number[],
    question: number[],
    solution: number[]
  },
  weeks: string[],
  total_contributions: number,
  total_questions: number,
  unique_users: number
}

export interface IConceptCreate {
  ontology_id?: string;
  description?: string;
  language: string;
  label: string
}

interface IGeneratedDescriptionResponse {
  description: string | null;
}

export interface IConceptSearchParams {
  limit?: number,
  query: string | null,
  exclude_unused?: boolean,
  exclude_ontology?: boolean,
  all_languages?: boolean,
  language_id?: string | string[]
}

export interface ITopConceptsResponse {
  values: {
    concept_id: string
    relations: string[]
  }[]
}

export interface ITopConnectedConceptsResponse {
  values: {
    concept_id: string
    relations: { concept_id: string; connections: number }[]
  }[]
}

export function conceptsApiFactory(api: AxiosInstance) {
  const baseUrl = '/api/v2/learning/concepts'
  const methods = {
    getConcepts(limit: number, offset: number) {
      return api.get<IGetConceptsResponse>(`${baseUrl}`, {
        params: {
          limit,
          offset
        }
      })
    },
    deleteConcept(conceptId: string) {
      return api.delete<any>(`${baseUrl}/${conceptId}`)
    },
    mergeConcepts(baseConceptId: string, mergedConceptId: string) {
      return api.put<void>(`${baseUrl}/merge`, {
        concept_id_to_keep: baseConceptId,
        concept_id_to_merge: mergedConceptId
      })
    },
    getBulkConcepts(conceptIds: string[]) {
      return api.post<{ values: IStandardizedConceptAllLanguages[] }>(`${baseUrl}/bulk-fetch`, {
        concept_ids: conceptIds
      })
    },
    getRelatedConcepts(conceptId: string) {
      return api.get<IStandardizedConceptAllLanguages[]>(`${baseUrl}/${conceptId}/related`)
    },
    searchTopicConcepts(searchParams?: IConceptSearchParams) {
      return api.get<IConceptSearchResultItem[]>(`${baseUrl}/labels`, {
        params: searchParams,
        paramsSerializer: {
          indexes: null
        }
      })
    },
    getConcept(id: string) {
      return api.get<IStandardizedConceptAllLanguages>(`${baseUrl}/${id}`)
    },
    getUserContributions(concept_id: string, isCurrentUser: boolean) {
      return api.get<IUserContributions>(`${baseUrl}/${concept_id}/contributions/statistics`, {
        params: {
          current_user: isCurrentUser
        }
      })
    },
    createConcept(obj: IConceptCreate) {
      return api.post<IStandardizedConceptAllLanguages>(baseUrl, obj)
    },
    async importConcept(obj: IConceptImport) {
      return api.post<IStandardizedConceptAllLanguages>(`${baseUrl}/import`, obj)
    },
    getGeneratedDescription(conceptId: string, language: string) {
      return api.get<IGeneratedDescriptionResponse>(`${baseUrl}/${conceptId}/descriptions/${language}/generated`)
    },
    acceptGeneratedDescription(conceptId: string, language: string) {
      return api.post<void>(`${baseUrl}/${conceptId}/descriptions/${language}/generated/accept`)
    },
    getTopConcepts(limit = 25) {
      return api.get<ITopConceptsResponse>(`${baseUrl}/top`, {
        params: {
          limit
        }
      })
    },
    getTopRelatedConcepts(conceptIds: string[]) {
      return api.get<ITopConnectedConceptsResponse>(`${baseUrl}/top-connected`, {
        params: {
          concept_id: conceptIds
        },
        paramsSerializer: {
          indexes: null
        }
      })
    },
    updateDescription(conceptId: string, language: string, description: string) {
      return api.put<void>(`${baseUrl}/${conceptId}/descriptions/${language}`, {
        description
      })
    },
    setPrimaryLabel(conceptId: string, language: string, label: string) {
      return api.put<void>(`${baseUrl}/${conceptId}/labels/${language}/${encodeURIComponent(label)}/is-primary`)
    },
    addLabel(conceptId: string, language: string, label: string) {
      return api.post<void>(`${baseUrl}/${conceptId}/labels`, {
        language,
        label
      })
    },
    deleteLabel(conceptId: string, language: string, label: string) {
      return api.delete<void>(`${baseUrl}/${conceptId}/labels/${language}/${encodeURIComponent(label)}`)
    },
    getLabels(conceptId: string) {
      return api.get<IConceptLabel[]>(`${baseUrl}/${conceptId}/labels`)
    },
    getDescriptions(conceptId: string) {
      return api.get<IConceptDescription[]>(`${baseUrl}/${conceptId}/descriptions`)
    },
    addParent(childConceptId: string, parentConceptId: string) {
      return api.put<void>(`${baseUrl}/relation`, {
        base: parentConceptId,
        relationship: ConceptRelations.IS_PARENT_OF,
        target: childConceptId
      })
    },
    deleteParent(childConceptId: string, parentConceptId: string) {
      return api.delete<void>(`${baseUrl}/relation`, {
        data: {
          base: parentConceptId,
          relationship: ConceptRelations.IS_PARENT_OF,
          target: childConceptId
        }
      })
    },
    excludeConcept(conceptId: string, topicIsExcluded: boolean) {
      return api.put<void>(`${baseUrl}/${conceptId}/exclude`, {
        is_excluded: topicIsExcluded
      })
    },
    suggestConcepts(text: string) {
      return api.post<IConceptSearchResultItem[]>(`${baseUrl}/suggestions`, { text })
    }
  }

  return createOptimizedAPI(methods)
}
